<template>
  <div
    class="ip-field"
    :class="{
      'ip-field--editable': editable && !safeLocked,
      'ip-field--locked': safeLocked,
      'ip-field--readonly': readonlyLook,
      'ip-field--active': active,
      'ip-field--saving': saving,
      'ip-field--bare': bare,
      'ip-field--missing': missing && !active,
    }"
    @click="activate"
  >
    <div v-if="showLabel" class="ip-field__label">
      {{ $t(field.label) }}<span v-if="editable && (field.required || required)" class="ip-field__required">*</span>
      <i
        v-if="missing"
        class="fas fa-exclamation-triangle ip-field__missing-icon"
        :title="$t('missingRequired.field')"
      ></i>
      <!-- Descripción del campo (tooltip de la definición), igual que en el listado -->
      <ColumnFieldHelpIcon
        v-if="field.tooltip"
        :tooltip="field.tooltip"
        :aria-label="$t('ColumnFieldHelpAria')"
        popover-prefer-right
      />
    </div>

    <div v-if="!active" class="ip-field__value">
      <template v-if="!isEmpty">
        <slot :value="value">
          <ViewContent
            v-if="field.type === 'group'"
            :key="groupViewKey"
            :fields="[field]"
            :value="viewItem"
            :visualGroups="[]"
            :activeVisualGroup="null"
            :lists="lists"
            :models="models"
            :selectedModel="modelName"
            :originalSelectedModel="modelName"
          />
          <component
            :is="viewComponent"
            v-else-if="viewComponent"
            :field="field"
            :value="value"
            mode="view"
            :item="item"
            :modelName="modelName"
            :lists="lists"
            :showLabel="false"
            :hideAddButton="true"
          />
          <span v-else>{{ value }}</span>
        </slot>
      </template>
      <!-- Número automático vacío: el «Generate» del formulario, a la vista -->
      <button
        v-else-if="editable && field.type === 'autoincrement'"
        type="button"
        class="btn btn-sm btn-primary mb-0 ip-field__generate"
        @click.stop="generateFromView"
      >
        <i class="fas fa-sync-alt me-1"></i>{{ $t("itemProfile.generateNumber") }}
      </button>
      <span v-else-if="missing" class="ip-field__empty ip-field__empty--missing">
        {{ $t("missingRequired.field") }}
      </span>
      <span v-else class="ip-field__empty">
        {{ editable ? $t("itemProfile.addValue") : "-" }}
      </span>
      <i v-if="saving" class="fas fa-spinner fa-spin ip-field__icon"></i>
      <!-- Campo con candado (safe): como en el formulario, se abre a propósito -->
      <button
        v-else-if="safeLocked"
        type="button"
        class="btn btn-link mb-0 p-0 ip-field__lock"
        :title="$t('itemProfile.unlockField')"
        @click.stop="$emit('activate', field.key)"
      >
        <i class="fas fa-lock"></i>
      </button>
      <i v-else-if="editable" class="fas fa-pen ip-field__icon"></i>
    </div>

    <div
      v-else
      ref="editor"
      class="ip-field__editor"
      @keydown.enter="onEnter"
      @keydown.esc.prevent="cancel"
    >
      <!-- Texto múltiple (emails, teléfonos): un renglón por valor -->
      <div v-if="isTextArray" class="ip-field__array">
        <div
          v-for="(entry, idx) in localValue"
          :key="'ip-arr-' + idx"
          class="ip-field__array-row"
        >
          <input
            :ref="'arrayInput' + idx"
            class="form-control form-control-sm"
            :type="field.type === 'email' ? 'email' : 'text'"
            :value="entry"
            @input="setArrayEntry(idx, $event.target.value)"
          />
          <button
            type="button"
            class="btn btn-sm btn-link text-danger px-1 mb-0"
            :title="$t('Delete')"
            @click="removeArrayEntry(idx)"
          >
            <i class="fas fa-times"></i>
          </button>
        </div>
        <button
          type="button"
          class="btn btn-sm btn-link px-0 mb-0"
          @click="addArrayEntry"
        >
          <i class="fas fa-plus me-1"></i>{{ $t("itemProfile.addAnother") }}
        </button>
      </div>
      <ItemProfileGroupEditor
        v-else-if="isGroupArray"
        :field="field"
        :value="value"
        :modelName="modelName"
        :lists="lists"
        @input="onGroupInput"
      />
      <component
        :is="editComponent"
        v-else-if="editComponent"
        ref="editorField"
        :field="field"
        :value="localValue"
        mode="edit"
        :fieldId="`ip-${field.key}`"
        :editingFields="field.key"
        :disableLock="true"
        :lists="lists"
        :record-item="item"
        :parentItem="item"
        :modelName="modelName"
        :showLabel="false"
        :localeValues="localeValues"
        @input="onEditorInput"
        @uploading="uploading = $event"
        @sibling-input="onSiblingInput"
        @updateDateField="onAutoIncrementDate"
        @invoice-number-preview="onInvoiceNumberPreview"
      />
      <div class="ip-field__actions">
        <button
          type="button"
          class="btn btn-sm btn-primary mb-0"
          :disabled="saving"
          @mousedown.prevent
          @click.stop="commit"
        >
          <i class="fas fa-check"></i>
        </button>
        <button
          type="button"
          class="btn btn-sm btn-outline-secondary mb-0"
          :disabled="saving"
          @mousedown.prevent
          @click.stop="cancel"
        >
          <i class="fas fa-times"></i>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import {
  FieldText,
  FieldNumber,
  FieldList,
  FieldReference,
  FieldDate,
  FieldBoolean,
  FieldFile,
  FieldTextList,
} from "@/components/gotrading/model-definition-core";
import {
  getFieldLocaleSiblingKey,
  getFieldTranslatableLocales,
  isFieldTranslatable,
} from "@/utils/fieldValueI18n";
import AutoIncrementField from "@/components/gotrading/common/AutoIncrementField.vue";
import ColumnFieldHelpIcon from "@/components/gotrading/common/ColumnFieldHelpIcon.vue";
import ItemProfileGroupEditor, { cleanGroupRows } from "./ItemProfileGroupEditor.vue";
import { getProfileLayout } from "./profileLayouts";

const VIEW_COMPONENTS = {
  string: "FieldText",
  text: "FieldText",
  email: "FieldText",
  textarea: "FieldText",
  number: "FieldNumber",
  currency: "FieldNumber",
  list: "FieldList",
  reference: "FieldReference",
  users: "FieldReference",
  date: "FieldDate",
  datetime: "FieldDate",
  boolean: "FieldBoolean",
  file: "FieldFile",
  image: "FieldFile",
  textlist: "FieldTextList",
};

// Lo que no está acá (imágenes, archivos, grupos simples) se edita en el formulario
// completo. Los grupos múltiples tienen su propio editor (ItemProfileGroupEditor).
const EDIT_COMPONENTS = {
  string: "FieldText",
  text: "FieldText",
  email: "FieldText",
  textarea: "FieldText",
  number: "FieldNumber",
  list: "FieldList",
  reference: "FieldReference",
  date: "FieldDate",
  boolean: "FieldBoolean",
  textlist: "FieldTextList",
  file: "FieldFile",
  // Numeración con botón «Generate», el mismo componente del formulario.
  autoincrement: "AutoIncrementField",
};

// Al elegir una opción ya está todo dicho: se guarda sin esperar al ✓.
const SAVE_ON_PICK = ["boolean", "list", "textlist", "date"];
// El adjunto se guarda apenas termina de subir (o al quitarlo), también si es múltiple:
// no hay nada más que tipear y esperar al ✓ dejaba el archivo subido sin guardar.
const SAVE_ON_UPLOAD = ["file"];

// Popups que se abren desde el editor pero se montan fuera de él.
const FLOATING_UI = ".modal, .swal2-container, .multiselect, .dropdown-menu, .flatpickr-calendar, .mx-datepicker-popup";

function idOf(v) {
  if (v && typeof v === "object" && v._id != null) return String(v._id);
  return v;
}

export function normalizeFieldValue(value) {
  if (Array.isArray(value)) return value.map(idOf);
  return idOf(value);
}

export function isEmptyFieldValue(value) {
  if (value === null || value === undefined || value === "") return true;
  if (Array.isArray(value)) return value.filter((v) => v !== null && v !== "").length === 0;
  return false;
}

function isGroupArrayField(field) {
  return field.type === "group" && !!field.isArray && Array.isArray(field.fields);
}

export function canInlineEdit(field) {
  return (
    (!!EDIT_COMPONENTS[field.type] || isGroupArrayField(field)) &&
    !field.readonly &&
    !field.hide
  );
}

export default {
  name: "ItemProfileField",
  components: {
    ViewContent: () => import("@/components/gotrading/common/ViewContent.vue"),
    ItemProfileGroupEditor,
    ColumnFieldHelpIcon,
    AutoIncrementField,
    FieldText,
    FieldNumber,
    FieldList,
    FieldReference,
    FieldDate,
    FieldBoolean,
    FieldFile,
    FieldTextList,
  },
  props: {
    field: { type: Object, required: true },
    item: { type: Object, required: true },
    lists: { type: Array, default: () => [] },
    models: { type: Array, default: () => [] },
    modelName: { type: String, required: true },
    /** La ficha está en modo edición (todos los campos muestran el contorno) */
    editing: { type: Boolean, default: false },
    /** Este campo es el que se está editando */
    active: { type: Boolean, default: false },
    saving: { type: Boolean, default: false },
    showLabel: { type: Boolean, default: true },
    /** Sin caja ni label: para el encabezado (nombre, chips) */
    bare: { type: Boolean, default: false },
    /** No se edita aunque el tipo lo permita: lo calcula una regla o el rol no edita el grupo. */
    blocked: { type: Boolean, default: false },
    /** Obligatorio para este registro, aunque la definición no lo marque: lo exige una regla «Hacer obligatorio» (#473). */
    required: { type: Boolean, default: false },
    /** Obligatorio y vacío (se guardó con «Guardar de todos modos»): en rojo para completarlo (#473). */
    missing: { type: Boolean, default: false },
  },
  data() {
    return {
      localValue: null,
      // Filas del grupo tal como llegaron al abrir el editor: contra eso se compara.
      groupBaseline: null,
      // Traducciones tocadas (name_en, name_es…): viajan junto con el campo.
      siblingChanges: {},
      // Un archivo a medio subir: confirmar ahora guardaría el valor de antes.
      uploading: false,
      // «Generate» tocado desde la vista: al abrir el editor se genera el número.
      generateOnOpen: false,
    };
  },
  computed: {
    value() {
      return this.item ? this.item[this.field.key] : null;
    },
    /** Filas que la vista no muestra (las define el layout de la ficha). */
    hiddenRowTest() {
      const layout = getProfileLayout(this.modelName);
      const test = layout && layout.hiddenViewRows && layout.hiddenViewRows[this.field.key];
      return typeof test === "function" && Array.isArray(this.value) ? test : null;
    },
    viewValue() {
      return this.hiddenRowTest ? this.value.filter((r) => !this.hiddenRowTest(r)) : this.value;
    },
    viewItem() {
      return this.hiddenRowTest ? { ...this.item, [this.field.key]: this.viewValue } : this.item;
    },
    /**
     * ViewContent busca las etiquetas de las referencias solo al montarse. Al guardar,
     * el editor se cierra antes de que llegue el item recargado: sin esta key la vista
     * se quedaba con el gasto anterior hasta el próximo montaje.
     */
    groupViewKey() {
      const stamp = (this.item && (this.item.updatedAt || this.item.__v)) || "";
      return `${stamp}|${JSON.stringify(this.viewValue || null)}`;
    },
    isEmpty() {
      return isEmptyFieldValue(this.viewValue);
    },
    editable() {
      return this.editing && !this.blocked && canInlineEdit(this.field);
    },
    /** Solo lectura, de calculado o sin permiso: gris como en el formulario. */
    readonlyLook() {
      return this.editing && !this.editable;
    },
    /** Candado (safe) con valor: el clic en el campo no lo abre, hay que tocar el candado. */
    safeLocked() {
      return this.editable && !!this.field.safe && !this.isEmpty && !this.active;
    },
    /** Valores de las traducciones para FieldText (campos translatable). */
    localeValues() {
      if (!isFieldTranslatable(this.field)) return {};
      const map = {};
      for (const code of getFieldTranslatableLocales(this.field)) {
        const k = getFieldLocaleSiblingKey(this.field.key, code);
        map[code] = this.item && this.item[k] != null ? String(this.item[k]) : "";
      }
      return map;
    },
    isGroupArray() {
      return isGroupArrayField(this.field);
    },
    isTextArray() {
      return (
        ["string", "text", "email"].includes(this.field.type) &&
        (this.field.isArray || Array.isArray(this.value))
      );
    },
    viewComponent() {
      return VIEW_COMPONENTS[this.field.type] || null;
    },
    editComponent() {
      return EDIT_COMPONENTS[this.field.type] || null;
    },
  },
  watch: {
    active: {
      immediate: true,
      handler(isActive) {
        if (isActive) {
          this.startEditing();
        } else {
          this.stopListening();
        }
      },
    },
  },
  beforeDestroy() {
    this.stopListening();
  },
  methods: {
    generateFromView() {
      this.generateOnOpen = true;
      this.$emit("activate", this.field.key);
    },
    activate() {
      if (!this.editable || this.active || this.safeLocked) return;
      this.$emit("activate", this.field.key);
    },
    startEditing() {
      this.groupBaseline = null;
      this.siblingChanges = {};
      if (this.isTextArray) {
        const current = Array.isArray(this.value)
          ? this.value.slice()
          : this.value
          ? [this.value]
          : [];
        this.localValue = current.length ? current : [""];
      } else {
        this.localValue = this.value;
      }
      document.addEventListener("mousedown", this.onOutsideMousedown, true);
      this.$nextTick(() => {
        if (this.generateOnOpen) {
          this.generateOnOpen = false;
          const comp = this.$refs.editorField;
          if (comp && typeof comp.generateNumber === "function") comp.generateNumber();
          return;
        }
        const el = this.$refs.editor;
        const input = el && el.querySelector("input:not([type=checkbox]), textarea");
        if (input) input.focus();
      });
    },
    stopListening() {
      document.removeEventListener("mousedown", this.onOutsideMousedown, true);
    },
    onOutsideMousedown(event) {
      const target = event.target;
      if (!target || this.$el.contains(target)) return;
      if (target.closest && target.closest(FLOATING_UI)) return;
      // Los inputs de texto avisan el valor en el blur, que llega después del mousedown.
      setTimeout(() => this.commit(), 0);
    },
    onSiblingInput({ path, value }) {
      if (path) this.$set(this.siblingChanges, path, value);
    },
    /** Generate también pone la fecha del número (<key>_date): viaja en el mismo guardado. */
    onAutoIncrementDate({ fieldKey, value }) {
      if (fieldKey) this.$set(this.siblingChanges, fieldKey, value);
    },
    /** Número estimado (se reserva al guardar), igual que en el formulario. */
    onInvoiceNumberPreview(isPreview) {
      this.$set(this.siblingChanges, "_invoiceNumberPreview", isPreview ? true : undefined);
    },
    onGroupInput(rows) {
      this.localValue = rows;
      if (this.groupBaseline === null) {
        this.groupBaseline = JSON.stringify(cleanGroupRows(rows, this.field.fields));
      }
    },
    onEditorInput(val) {
      this.localValue = val;
      if (
        (SAVE_ON_PICK.includes(this.field.type) && !this.field.isArray) ||
        SAVE_ON_UPLOAD.includes(this.field.type)
      ) {
        this.$nextTick(() => this.commit());
      }
    },
    onEnter(event) {
      if (event.target && event.target.tagName === "TEXTAREA") return;
      event.preventDefault();
      // Forzar el blur para que FieldText/FieldNumber emitan el valor tipeado.
      if (document.activeElement) document.activeElement.blur();
      this.$nextTick(() => this.commit());
    },
    setArrayEntry(idx, val) {
      this.$set(this.localValue, idx, val);
    },
    addArrayEntry() {
      this.localValue.push("");
      const idx = this.localValue.length - 1;
      this.$nextTick(() => {
        const ref = this.$refs["arrayInput" + idx];
        const input = Array.isArray(ref) ? ref[0] : ref;
        if (input) input.focus();
      });
    },
    removeArrayEntry(idx) {
      this.localValue.splice(idx, 1);
    },
    commit() {
      if (!this.active || this.saving) return;
      // FieldText/FieldNumber avisan el valor en el blur, y el ✓ no le saca el foco al input.
      const focused = document.activeElement;
      if (focused && focused !== document.body && this.$el.contains(focused)) {
        focused.blur();
        this.$nextTick(() => this.commitValue());
        return;
      }
      this.commitValue();
    },
    commitValue() {
      if (!this.active || this.saving || this.uploading) return;
      if (this.isGroupArray) {
        const rows = cleanGroupRows(this.localValue, this.field.fields);
        if (this.groupBaseline === null || JSON.stringify(rows) === this.groupBaseline) {
          this.$emit("deactivate", this.field.key);
          return;
        }
        this.$emit("save", { key: this.field.key, value: rows });
        return;
      }
      let next = this.localValue;
      if (this.isTextArray) {
        next = (next || []).map((v) => String(v || "").trim()).filter(Boolean);
      }
      next = normalizeFieldValue(next);
      const prev = normalizeFieldValue(this.value);
      const same =
        JSON.stringify(next === undefined ? null : next) ===
          JSON.stringify(prev === undefined ? null : prev) ||
        (isEmptyFieldValue(next) && isEmptyFieldValue(prev));
      const extra = {};
      for (const [path, val] of Object.entries(this.siblingChanges)) {
        const before = this.item && this.item[path] != null ? String(this.item[path]) : "";
        if (String(val || "") !== before) extra[path] = val;
      }
      if (same && !Object.keys(extra).length) {
        this.$emit("deactivate", this.field.key);
        return;
      }
      this.$emit("save", { key: this.field.key, value: next, extra });
    },
    cancel() {
      this.$emit("deactivate", this.field.key);
    },
  },
};
</script>

<style scoped>
.ip-field {
  position: relative;
  border: 1px dashed transparent;
  border-radius: 0.5rem;
  padding: 0.35rem 0.5rem;
  margin: 0 -0.5rem;
  transition: border-color 0.15s, background-color 0.15s;
  min-width: 0;
}

.ip-field__label {
  font-size: 0.72rem;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--bs-secondary-color);
  margin-bottom: 0.1rem;
}

.ip-field__value {
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  min-height: 1.5rem;
  word-break: break-word;
}

.ip-field__value > :first-child {
  flex: 1 1 auto;
  min-width: 0;
}

.ip-field__empty {
  color: var(--bs-secondary-color);
  font-style: italic;
}

.ip-field__icon {
  flex: 0 0 auto;
  font-size: 0.75rem;
  color: var(--bs-primary);
  opacity: 0;
  margin-top: 0.3rem;
  transition: opacity 0.15s;
}

/* La ficha se edita siempre: el contorno y el lápiz aparecen al pasar el mouse. */
.ip-field--editable {
  cursor: pointer;
}

.ip-field--editable:hover {
  border-color: rgba(var(--bs-primary-rgb), 0.6);
  background-color: rgba(var(--bs-primary-rgb), 0.06);
}

.ip-field--editable:hover .ip-field__icon,
.ip-field--saving .ip-field__icon {
  opacity: 1;
}

/* Teléfono: no hay hover, el lápiz queda a la vista. */
@media (hover: none) {
  .ip-field--editable .ip-field__icon {
    opacity: 1;
  }
}

.ip-field--active {
  border: 1px solid var(--bs-primary);
  background-color: var(--bs-body-bg);
  box-shadow: 0 0 0 0.2rem rgba(var(--bs-primary-rgb), 0.15);
  cursor: default;
  z-index: 3;
}

.ip-field__editor {
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
}

.ip-field__actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.35rem;
}

.ip-field__array {
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
  align-items: flex-start;
}

.ip-field__array-row {
  display: flex;
  align-items: center;
  width: 100%;
  gap: 0.25rem;
}

.ip-field--bare {
  margin: 0;
}

.ip-field--bare:not(.ip-field--editable):not(.ip-field--active) {
  padding: 0;
}

.ip-field__value :deep(ul) {
  padding-left: 0;
  margin: 0;
  list-style: none;
}

.ip-field__value :deep(p:last-child) {
  margin-bottom: 0;
}

/* Ayuda (?) del campo: siempre a la vista y con el color del rótulo (el estilo base es
   para la cabecera oscura del listado, donde aparece al pasar el mouse). */
.ip-field__label :deep(.column-field-help-btn) {
  opacity: 0.7;
  padding: 0 0 0 0.2rem;
  color: var(--bs-primary);
  font-size: 0.85rem;
}

.ip-field__label :deep(.column-field-help-btn:hover) {
  opacity: 1;
  color: var(--bs-primary);
}

.ip-field__generate {
  flex: 0 0 auto !important;
  padding: 0.2rem 0.6rem;
  font-size: 0.8rem;
}

.ip-field__required {
  margin-left: 0.15rem;
  color: var(--bs-danger);
}

/* Obligatorio vacío: contorno y texto en rojo, a la vista sin pasar el mouse (#473) */
.ip-field--missing,
.ip-field--missing.ip-field--editable:hover {
  border-color: rgba(var(--bs-danger-rgb), 0.55);
  background-color: rgba(var(--bs-danger-rgb), 0.05);
}

.ip-field__missing-icon {
  margin-left: 0.3rem;
  font-size: 0.8rem;
  color: var(--bs-danger);
}

.ip-field__empty.ip-field__empty--missing {
  color: var(--bs-danger);
  font-style: normal;
  font-weight: 600;
}

/* Solo lectura / calculado: el gris del formulario (readonly-field) */
.ip-field--readonly:not(.ip-field--bare) {
  background-color: var(--bs-tertiary-bg);
  border: 1px solid var(--bs-border-color);
  cursor: not-allowed;
}

.ip-field--readonly .ip-field__value {
  color: var(--bs-secondary-color);
}

.ip-field__lock {
  flex: 0 0 auto;
  font-size: 0.8rem;
  line-height: 1;
  color: var(--bs-warning);
}

.ip-field__lock:hover {
  color: var(--bs-primary);
}

#darkdesign .ip-field--readonly:not(.ip-field--bare) {
  background-color: var(--dark-bg-tertiary);
  border-color: var(--dark-border-color);
}

#darkdesign .ip-field--readonly .ip-field__value {
  color: var(--dark-text-secondary);
}

/* dark.css no redefine --bs-secondary-color */
#darkdesign .ip-field__label,
#darkdesign .ip-field__empty {
  color: var(--dark-text-secondary);
}

#darkdesign .ip-field--active {
  background-color: var(--dark-bg-card);
}

#darkdesign .ip-field__empty.ip-field__empty--missing {
  color: var(--bs-danger);
}
</style>
